import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  ropV2AppEventManager,
  type RopV2AppApplicationInfoMessage,
  type RopV2AppPushDeviceIdMessage,
  type RopV2AppPushNotificationMessage,
  type RopV2AppPushNotificationResponseMessage,
  type RopV2AppPushTokenMessage,
} from "../helpers/RopV2AppEventManager";
import type {
  PushNotification,
  PushNotificationResponse,
} from "../models/push-notification.model";
import { usersApi } from "../../../services/api";
import { MainContext } from "../../../main/context/MainContextProvider";

export type AppInfo = {
  applicationName: string | null;
  nativeApplicationVersion: string | null;
  nativeBuildVersion: string | null;
};

type RopV2AppNativeContextProps = {
  appInfo: AppInfo;
  pushToken: string;
  pushDeviceId: string;
  lastNotification: PushNotification | null;
  lastOpenedNotification: PushNotificationResponse | null;
  notifications: PushNotification[];
  openedNotifications: PushNotificationResponse[];
  // tryLinkCurrentMobileDevice: () => Promise<boolean>,
  // tryUnlinkCurrentMobileDevice: () => Promise<boolean>,
};

type FaceSchoolAppNativeProviderProps = {
  children: ReactNode;
};

const RopV2AppNativeContext = createContext<RopV2AppNativeContextProps>({
  appInfo: {
    applicationName: null,
    nativeApplicationVersion: null,
    nativeBuildVersion: null,
  },
  pushToken: "",
  pushDeviceId: "",
  lastNotification: null,
  lastOpenedNotification: null,
  notifications: [],
  openedNotifications: [],
  // tryLinkCurrentMobileDevice: () => new Promise(resolve => resolve(false)),
  // tryUnlinkCurrentMobileDevice: () => new Promise(resolve => resolve(false)),
});

export function RopV2AppNativeProvider({
  children,
}: FaceSchoolAppNativeProviderProps) {
  const [appInfo, setAppInfo] = useState<AppInfo>({
    applicationName: null,
    nativeApplicationVersion: null,
    nativeBuildVersion: null,
  });

  const [pushToken, setPushToken] = useState("");
  const [pushDeviceId, setPushDeviceId] = useState("");

  const [lastNotification, setLastNotification] =
    useState<PushNotification | null>(null);
  const [lastOpenedNotification, setLastOpenedNotification] =
    useState<PushNotificationResponse | null>(null);

  const [notifications, setNotifications] = useState<PushNotification[]>([]);
  const [openedNotifications, setOpenedNotifications] = useState<
    PushNotificationResponse[]
  >([]);

  const { user } = useContext(MainContext)

  function onRopV2AppApplicationInfoMsg(info: RopV2AppApplicationInfoMessage) {
    console.debug("RopV2App info:", info);
    setAppInfo(info.data);
  }

  function onRopV2AppPushTokenMsg(msg: RopV2AppPushTokenMessage) {
    console.debug("RopV2App pushToken:", msg);
    setPushToken(msg.data);
  }

  function onRopV2AppPushTokenDeviceIdMsg(msg: RopV2AppPushDeviceIdMessage) {
    console.debug("RopV2App pushDeviceId:", msg);
    setPushDeviceId(msg.data);
  }

  function onRopV2AppNotificationReceivedMsg(
    msg: RopV2AppPushNotificationMessage
  ) {
    console.debug("RopV2App notification:", msg);
    setLastNotification(msg.data);
    setNotifications((prev) => [...prev, msg.data]);
  }

  function onRopV2AppNotificationResponseReceivedMsg(
    msg: RopV2AppPushNotificationResponseMessage
  ) {
    console.debug("RopV2App notification response:", msg);
    setLastOpenedNotification(msg.data);
    setOpenedNotifications((prev) => [...prev, msg.data]);
  }

  useEffect(() => {
    /*
      XXX: Se o callback de um evento depende de um estado, o useEffect
      deve ser chamado deve possuir o estado como dependência e o callback
      deve ser parado
    */

    console.debug("RopV2AppNativeProvider iniciado.");

    // Informa ao WebView Nativo o status do WebApp.
    // O WebView Nativo usa esse status para envar eventos somente
    // quando o WebApp estiver completamente carregado.
    ropV2AppEventManager.sendWebAppStatusEvent("INITIALIZED");
    ropV2AppEventManager.on("ropV2AppInfo", onRopV2AppApplicationInfoMsg);
    ropV2AppEventManager.on("ropV2AppPushToken", onRopV2AppPushTokenMsg);
    ropV2AppEventManager.on(
      "ropV2AppPushDeviceId",
      onRopV2AppPushTokenDeviceIdMsg
    );
    ropV2AppEventManager.on(
      "ropV2AppPushNotification",
      onRopV2AppNotificationReceivedMsg
    );
    ropV2AppEventManager.on(
      "ropV2AppPushNotificationResponse",
      onRopV2AppNotificationResponseReceivedMsg
    );

    return () => {
      console.debug("RopV2AppNativeProvider finalizado.");

      ropV2AppEventManager.sendWebAppStatusEvent("FINISHED");
      ropV2AppEventManager.off("ropV2AppInfo", onRopV2AppApplicationInfoMsg);
      ropV2AppEventManager.off("ropV2AppPushToken", onRopV2AppPushTokenMsg);
      ropV2AppEventManager.off(
        "ropV2AppPushDeviceId",
        onRopV2AppPushTokenDeviceIdMsg
      );
      ropV2AppEventManager.off(
        "ropV2AppPushNotification",
        onRopV2AppNotificationReceivedMsg
      );
      ropV2AppEventManager.off(
        "ropV2AppPushNotificationResponse",
        onRopV2AppNotificationResponseReceivedMsg
      );

      usersApi.updateUserLastActivity(user?.userId ?? "")
    };
  }, []);

  return (
    <RopV2AppNativeContext.Provider
      value={{
        appInfo,
        pushToken,
        pushDeviceId,
        lastNotification,
        lastOpenedNotification,
        notifications,
        openedNotifications,
        // tryLinkCurrentMobileDevice,
        // tryUnlinkCurrentMobileDevice,
      }}
    >
      {/* DEBUG */}
      {/* <>
        <div
          style={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <span style={{ color: "#f00" }}>
            {appInfo.applicationName} - AppVer:{" "}
            {appInfo.nativeApplicationVersion} - BuildVer:{" "}
            {appInfo.nativeBuildVersion}
          </span>
        </div>
      </> */}

      {children}
    </RopV2AppNativeContext.Provider>
  );
}

// function sendTestPushNotification(
//     title = "title",
//     body = "body",
//     type = "NOTIFICATION_RECEIVED",
//     pushData = {}
// ) {
//     // types:
//     // NOTIFICATION_RECEIVED
//     // NOTIFICATION_RESPONSE_RECEIVED

//     // O nativo faz um postMessage no formato:
//     // {
//     //     "runningInRopV2App": true,
//     //     "type": "NOTIFICATION_RECEIVED",
//     //     "data": {
//     //         "body": "stringBody",
//     //         "data": {
//     //             "withSome": "data"
//     //         },
//     //         "date": 1657825676429,
//     //         "identifier": "0:1657825676435128%0ac519e6f9fd7ecd",
//     //         "title": "stringTitle"
//     //     }
//     // }

//     // Engana o WebApp dizendo que ele está rodando dentro do FaceSchoolNativeApp
//     window.runningInRopV2App = true;
//     const data = {
//         runningInRopV2App: true,
//         type,
//         data: {
//             // TODO: Gerar aleatorio android e ios
//             identifier: "0:1657825676435128%0ac519e6f9fd7ecd",
//             title,
//             body,
//             data: pushData,
//             date: new Date().toISOString(),
//         },
//     };
//     const dataJSONb64 = window.btoa(JSON.stringify(data));
//     window.postMessage(dataJSONb64);
// }

// function debugSetDevicePushTokenId(pushDeviceId = "12345") {
//   // Engana o WebApp dizendo que ele está rodando dentro do FaceSchoolNativeApp
//   window.ReactNativeWebView = {
//     postMessage(message: string) {
//       console.debug("DEBUG: window.ReactNativeWebView.postMessage", message);
//     },
//   };
//   const data = {
//     isRopV2AppEvent: true,
//     type: "PUSH_DEVICE_ID",
//     data: pushDeviceId,
//   };
//   window.postMessage(data);
// }

// // Disponibiliza a função globalmente para uso
// window.___sendTestPushNotification = sendTestPushNotification;
// window.___debugSetDevicePushTokenId = debugSetDevicePushTokenId;

export function useRopV2AppNative() {
  return useContext(RopV2AppNativeContext);
}
